import {
    ACTION_TYPES,
    createAction,
    EVENT_TYPE,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { ICheckAvailability } from "./ICheckAvailability";
import { getDeliveryDate, getDeliveryEstimate, validatePincode } from "./utils";

const initialData = {
    status: false,
    message: "",
    shippingDays: 0,
};

const useCheckAvailability = ({
    disablePayment,
}: ICheckAvailability.IProps) => {
    const { eventData } = useEventDataOfSelectedEvent();
    const {
        uuid: listingUuid,
        variantsData,
        selectedVariant,
        uuid: eventId,
        type: eventType,
        enable_shiprocket: shiprocketEnabled,
    } = eventData;

    const dispatch = useDispatch();

    const [pincode, setPincode] = useState("");
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [servicable, setServicable] = useState(initialData);

    const hasVariants = !!variantsData?.variants;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setError(validatePincode(value));
        setPincode(value);
        setServicable(initialData);

        // reset pincode if user entering new one
        dispatch(
            createAction(ACTION_TYPES.EVENT.SET_SHIPPING_DETAILS, {
                payload: {
                    eventId,
                    data: {
                        pincode: undefined,
                    },
                },
            })
        );
    };

    const handleBlur = () => setShowError(true);

    const getShippingInfo = (inputPincode?: string) => {
        getDeliveryEstimate({
            listingUuid,
            variationUuid: selectedVariant?.uuid,
            pincode: inputPincode,
            currency: eventData?.currency_name,
            isInternationalEnabled: eventData?.is_international_enabled,
        })
            .then(data => {
                const {
                    shipping_days: shippingDays,
                    shipping_charge: shippingCharge,
                } = data;
                setServicable({
                    status: true,
                    message: `Estimated Delivery by ${getDeliveryDate(
                        shippingDays
                    )}`,
                    shippingDays,
                });
                // shipping charge will be dyanmic due to shiprocket
                // add verified pincode in shipping details
                dispatch(
                    createAction(ACTION_TYPES.EVENT.SET_SHIPPING_DETAILS, {
                        payload: {
                            eventId,
                            data: {
                                shipping_charge: shippingCharge,
                                pincode: inputPincode,
                            },
                        },
                    })
                );
            })
            .catch(() => {
                setServicable({
                    status: false,
                    message: "Pincode is not servicable.",
                    shippingDays: 0,
                });
                // remove pincode
                dispatch(
                    createAction(ACTION_TYPES.EVENT.SET_SHIPPING_DETAILS, {
                        payload: {
                            eventId,
                            data: { pincode: undefined },
                        },
                    })
                );
            })
            .finally(() => setError(""));
    };

    const handleCheckEstimate = () => {
        if (hasVariants && !selectedVariant) {
            setServicable({
                status: false,
                message: "Select product variant",
                shippingDays: 0,
            });
        }
        if (!pincode) {
            setError("Required");
            setShowError(true);
        }
        if ((hasVariants && !selectedVariant) || !pincode) return;
        getShippingInfo(pincode);
    };

    const isMerchandise = eventType === EVENT_TYPE.MERCHANDISE;
    const hideComponent =
        !isMerchandise ||
        disablePayment ||
        (!shiprocketEnabled && !servicable.shippingDays); // shiprocket is disabled or if the pincode is not servicable

    useEffect(() => {
        // if payment is enabled and shiprocket is not enabled then any pincode is allowed, we just need to show shipping chage and shipping days
        if (!shiprocketEnabled && isMerchandise) getShippingInfo();
    }, [eventData?.is_international_enabled]);

    useEffect(() => {
        if (pincode) getShippingInfo(pincode);
    }, [selectedVariant]);

    return {
        handleChange,
        handleBlur,
        handleCheckEstimate,

        pincode,
        error,
        showError,
        servicable,
        hideComponent,
        shiprocketEnabled,
    };
};

export default useCheckAvailability;
